import { useQuery } from '@tanstack/react-query';
import { DraftResults, DraftSummary, Franchise, Season } from '../types/fantasy';

// API functions
const fetchFranchises = async (): Promise<Franchise[]> => {
  const response = await fetch('/data/franchises/index.json');
  if (!response.ok) {
    throw new Error('Failed to fetch franchises');
  }
  return response.json();
};

const fetchSeasons = async (): Promise<Season[]> => {
  const response = await fetch('/data/seasons/index.json');
  if (!response.ok) {
    throw new Error('Failed to fetch seasons');
  }
  return response.json();
};

const fetchDraftIndex = async (): Promise<DraftSummary[]> => {
  const response = await fetch('/data/drafts/index.json');
  if (!response.ok) {
    throw new Error('Failed to fetch draft index');
  }
  return response.json();
};

const fetchDraftResults = async (year: string): Promise<DraftResults> => {
  const response = await fetch(`/data/drafts/${year}.json`);
  if (!response.ok) {
    throw new Error(`Draft results not found for ${year}`);
  }
  return response.json();
};

const fetchWrappedData = async (franchiseId: string, year: string): Promise<any> => {
  const response = await fetch(`/data/wrapped/franchises/${franchiseId}_${year}.json`);
  if (!response.ok) {
    throw new Error(`Wrapped data not found for ${year}`);
  }
  return response.json();
};

// React Query hooks
export const useFranchises = () => {
  return useQuery({
    queryKey: ['franchises'],
    queryFn: fetchFranchises,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
  });
};

export const useSeasons = () => {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: fetchSeasons,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
  });
};

export const useDraftIndex = () => {
  return useQuery({
    queryKey: ['drafts'],
    queryFn: fetchDraftIndex,
    staleTime: 1000 * 60 * 30,
    retry: 1,
  });
};

export const useDraftResults = (year?: string) => {
  return useQuery({
    queryKey: ['draft', year],
    queryFn: () => fetchDraftResults(year!),
    enabled: !!year,
    staleTime: 1000 * 60 * 30,
    retry: 1,
  });
};

export const useWrappedData = (franchiseId?: string, year?: string) => {
  return useQuery({
    queryKey: ['wrapped', franchiseId, year],
    queryFn: () => fetchWrappedData(franchiseId!, year!),
    enabled: !!(franchiseId && year),
    staleTime: 1000 * 60 * 30, // 30 minutes - wrapped data changes less frequently
    retry: 1,
  });
};